import { VORABPAUSCHALE } from "@/lib/config";

export type FundType = "equity" | "mixed" | "realEstate" | "foreignRealEstate" | "other";

export const FUND_TYPE_LABELS: Record<FundType, string> = {
  equity: "Aktienfonds (≥ 51 % Aktienquote)",
  mixed: "Mischfonds (≥ 25 % Aktienquote)",
  realEstate: "Immobilienfonds (Inland)",
  foreignRealEstate: "Immobilienfonds (≥ 51 % Auslandsimmobilien)",
  other: "Sonstiger Fonds (z. B. Renten-/Geldmarktfonds)",
};

export interface VorabpauschaleInput {
  /** Wert des Fondsanteils zu Beginn des Steuerjahres (bzw. am Kauftag bei
   * unterjaehrigem Erwerb). */
  valueStartOfYear: number;
  /** Wert des Fondsanteils am Jahresende (bzw. am Verkaufstag). */
  valueEndOfYear: number;
  /** Tatsaechlich ausgeschuettete Betraege waehrend des Jahres (bei rein
   * thesaurierenden Fonds i. d. R. 0). */
  distributions: number;
  /** Basiszins gemaess § 18 Abs. 4 InvStG, siehe VORABPAUSCHALE.basiszins in
   * lib/config.ts (jaehrlich vom BMF veroeffentlicht, nicht live abrufbar). */
  basiszins: number;
  fundType: FundType;
  /** Volle Monate im Steuerjahr gehalten (1-12) - § 18 Abs. 2 InvStG kuerzt den
   * Basisertrag anteilig bei unterjaehrigem Erwerb. */
  monthsHeld: number;
}

export interface VorabpauschaleResult {
  /** Fondswert Jahresanfang x Basiszins x 0,7, anteilig nach gehaltenen Monaten. */
  basisertrag: number;
  /** Basisertrag, gedeckelt auf die tatsaechliche Wertsteigerung, abzueglich bereits
   * ausgeschuetteter Betraege - nie negativ. */
  vorabpauschale: number;
  partialExemptionRate: number;
  /** Vorabpauschale nach Abzug der Teilfreistellung (§ 20 InvStG). */
  taxableAmount: number;
  /** Grobe Schaetzung: taxableAmount x (Abgeltungsteuer + Soli), OHNE
   * Sparerpauschbetrag (personen-/depotuebergreifend, hier nicht bekannt) und OHNE
   * Kirchensteuer (individuell). Siehe Disclaimer in der UI. */
  estimatedTax: number;
}

/**
 * Deutsche Vorabpauschale fuer thesaurierende/teil-thesaurierende Fonds (§ 18 InvStG,
 * backlog.md -> Punkt 12). Formel: Basisertrag = Fondswert Jahresanfang x Basiszins x
 * 0,7, anteilig gekuerzt bei unterjaehrigem Erwerb (§ 18 Abs. 2), gedeckelt auf die
 * tatsaechliche Wertsteigerung im Jahr (§ 18 Abs. 1 Satz 2) und vermindert um bereits
 * ausgeschuettete Betraege. Bei Wertverlust oder negativem Basiszins ist die
 * Vorabpauschale 0, nie negativ. Reine Funktion, kein I/O - Basiszins und
 * Teilfreistellungssatz kommen als Parameter/aus lib/config.ts, keine eigene
 * Steuerberatung, siehe Disclaimer in der aufrufenden UI-Komponente.
 */
export function computeVorabpauschale(input: VorabpauschaleInput): VorabpauschaleResult | undefined {
  const { valueStartOfYear, valueEndOfYear, distributions, basiszins, fundType, monthsHeld } = input;
  if (
    valueStartOfYear < 0 ||
    valueEndOfYear < 0 ||
    !Number.isInteger(monthsHeld) ||
    monthsHeld < 1 ||
    monthsHeld > 12
  ) {
    return undefined;
  }

  const partialExemptionRate = VORABPAUSCHALE.partialExemptionRates[fundType];

  if (valueStartOfYear === 0) {
    return { basisertrag: 0, vorabpauschale: 0, partialExemptionRate, taxableAmount: 0, estimatedTax: 0 };
  }

  const basisertrag = Math.max(0, valueStartOfYear * basiszins * 0.7 * (monthsHeld / 12));
  const wertsteigerung = Math.max(0, valueEndOfYear - valueStartOfYear);
  const cappedBasisertrag = Math.min(basisertrag, wertsteigerung);
  const vorabpauschale = Math.max(0, cappedBasisertrag - Math.max(0, distributions));
  const taxableAmount = vorabpauschale * (1 - partialExemptionRate);
  const estimatedTax = taxableAmount * VORABPAUSCHALE.flatTaxRate;

  return { basisertrag, vorabpauschale, partialExemptionRate, taxableAmount, estimatedTax };
}
